import { useCoreLogger } from './useCoreLogger'
import { navigateTo, useRequestURL } from '#app'
import type { NavigateToOptions } from '#app/composables/router'

export const useSafeRedirect = () => {
    const logger = useCoreLogger()
    const currentUrl = useRequestURL()

    /**
     *
     * @param url A url that will be checked if it is safe to redirect to.
     * @returns True if the url is safe otherwise false.
     */
    function isSafe(url: string): boolean {
        try {
            const parsedUrl = new URL(url, currentUrl.origin)

            return currentUrl.origin === parsedUrl.origin
        }
        catch (err) {
            logger.error(err)

            return false
        }
    }

    /**
     *
     * @param url A url to redirect to
     * @param options Optional NavigateTo options. Defaults to external and redirecting with code 302.
     * @returns A promise that resolves to the return of call to navigateTo, if the url was redirected or false if the url is not safe for redirecting.
     */
    async function redirect(url: string, options: NavigateToOptions = {}): Promise<ReturnType<typeof navigateTo> | false> {
        const safe = isSafe(url)

        if (!safe) {
            return false
        }

        const mergedOptions = {
            external: true,
            redirectCode: 302,
            ...options,
        }

        return navigateTo(url, mergedOptions)
    }

    return {
        isSafe,
        redirect,
    }
}
